import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from './api';
import Spinner from './Spinner';
import CustomAlert from './CustomAlert';
import ExcelJS from 'exceljs';
import partTypeMap from './partTypeMap.json';
import {
    buildAircraftAk0RequisitionMap,
    filterAk0Rows,
    isAircraftSingleHitAgg,
    isAircraftSingleHitForPartAgg,
} from './ak0Utils';
import { useSiteNames } from './useSiteCatalog';

// Returns 'Consumable' | 'Repairable' | '' (unknown) for a given part number.
// Matches the normalization used elsewhere (whitespace stripped, upper-cased).
const normalizePartNo = (p) => (p == null ? '' : String(p).replace(/\s+/g, '').toUpperCase());
const lookupPartType = (partNo) => partTypeMap[normalizePartNo(partNo)] || '';

const PART_TYPE_OPTIONS = ['Consumable', 'Repairable', 'Unknown'];

// Background/foreground colors for the Part Type badge.
const PART_TYPE_COLORS = {
    Consumable: { bg: '#28a745', fg: '#ffffff', argb: 'FF28A745' },
    Repairable: { bg: '#fd7e14', fg: '#ffffff', argb: 'FFFD7E14' },
    Unknown:    { bg: '#6c757d', fg: '#ffffff', argb: 'FF6C757D' },
};

const ALL_SITES = 'ALL';

const EDD_STATUS = {
    EXPIRED: 'EDD Expired',
    DUE_30: 'Due within 30 Days',
    DUE_60: 'Due within 60 Days',
    DUE_90: 'Due within 90 Days',
    DUE_90_PLUS: 'Due 90+ Days',
    NO_EDD: 'No EDD Provided',
};

const EDD_STATUS_OPTIONS = [
    EDD_STATUS.EXPIRED,
    EDD_STATUS.DUE_30,
    EDD_STATUS.DUE_60,
    EDD_STATUS.DUE_90,
    EDD_STATUS.DUE_90_PLUS,
    EDD_STATUS.NO_EDD,
];

// Background/foreground colors for the EDD status badge.
const EDD_STATUS_COLORS = {
    [EDD_STATUS.EXPIRED]:     { bg: '#dc3545', fg: '#ffffff', argb: 'FFDC3545' },
    [EDD_STATUS.DUE_30]:      { bg: '#28a745', fg: '#ffffff', argb: 'FF28A745' },
    [EDD_STATUS.DUE_60]:      { bg: '#17a2b8', fg: '#ffffff', argb: 'FF17A2B8' },
    [EDD_STATUS.DUE_90]:      { bg: '#ffc107', fg: '#212529', argb: 'FFFFC107' },
    [EDD_STATUS.DUE_90_PLUS]: { bg: '#fd7e14', fg: '#ffffff', argb: 'FFFD7E14' },
    [EDD_STATUS.NO_EDD]:      { bg: '#6c757d', fg: '#ffffff', argb: 'FF6C757D' },
};

const getStyles = (theme) => {
    const isDark = theme === 'dark';
    const colors = {
        bg: isDark ? '#121212' : '#f4f7f6',
        cardBg: isDark ? '#1e1e1e' : '#fff',
        text: isDark ? '#e0e0e0' : '#333',
        subtleText: isDark ? '#a0a0a0' : '#666',
        border: isDark ? '#444' : '#ddd',
        shadow: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        link: isDark ? '#6cb2f7' : '#007bff',
        headerBg: isDark ? '#2a2a2a' : '#e9eef3',
        rowAlt: isDark ? '#262626' : '#fafbfc',
    };

    return {
        page: {
            backgroundColor: colors.bg,
            minHeight: '100vh',
            padding: '20px',
            color: colors.text,
        },
        headerCard: {
            backgroundColor: colors.cardBg,
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '20px',
            boxShadow: `0 2px 5px ${colors.shadow}`,
            border: `1px solid ${colors.border}`,
        },
        navLink: {
            color: colors.link,
            textDecoration: 'none',
            fontSize: '14px',
            display: 'inline-block',
            marginBottom: '10px',
        },
        title: {
            fontSize: '24px',
            fontWeight: 'bold',
            color: colors.text,
            margin: '0 0 6px 0',
        },
        subtitle: {
            fontSize: '14px',
            color: colors.subtleText,
            margin: 0,
        },
        section: {
            backgroundColor: colors.cardBg,
            borderRadius: '8px',
            boxShadow: `0 2px 5px ${colors.shadow}`,
            border: `1px solid ${colors.border}`,
            overflow: 'hidden',
        },
        sectionHeader: {
            padding: '15px 20px',
            borderBottom: `2px solid ${colors.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: colors.headerBg,
        },
        sectionTitle: {
            fontSize: '18px',
            fontWeight: 'bold',
            color: colors.text,
            margin: 0,
        },
        controls: {
            display: 'flex',
            gap: '12px',
            padding: '12px 20px',
            borderBottom: `1px solid ${colors.border}`,
            flexWrap: 'wrap',
            alignItems: 'center',
        },
        input: {
            padding: '8px 12px',
            borderRadius: '4px',
            border: `1px solid ${colors.border}`,
            backgroundColor: isDark ? '#333' : '#fff',
            color: colors.text,
            minWidth: '220px',
        },
        select: {
            padding: '8px 12px',
            borderRadius: '4px',
            border: `1px solid ${colors.border}`,
            backgroundColor: isDark ? '#333' : '#fff',
            color: colors.text,
        },
        exportButton: {
            padding: '8px 16px',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '13px',
        },
        tableWrap: {
            overflowX: 'auto',
        },
        scrollableTableWrap: {
            overflowX: 'auto',
            overflowY: 'auto',
            flex: 1,
            // Match stats panel height so the two columns line up evenly.
            minHeight: '560px',
            maxHeight: '560px',
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse',
        },
        th: {
            position: 'sticky',
            top: 0,
            padding: '12px 10px',
            textAlign: 'left',
            fontSize: '12px',
            fontWeight: 'bold',
            color: colors.text,
            backgroundColor: colors.headerBg,
            borderBottom: `2px solid ${colors.border}`,
            cursor: 'pointer',
            userSelect: 'none',
            whiteSpace: 'nowrap',
        },
        thNum: {
            textAlign: 'right',
        },
        td: {
            padding: '8px 10px',
            fontSize: '12px',
            color: colors.text,
            borderBottom: `1px solid ${colors.border}`,
            verticalAlign: 'top',
        },
        tdNum: {
            textAlign: 'right',
            fontVariantNumeric: 'tabular-nums',
        },
        rowAlt: {
            backgroundColor: colors.rowAlt,
        },
        totalRow: {
            backgroundColor: colors.headerBg,
            fontWeight: 'bold',
            borderTop: `2px solid ${colors.border}`,
        },
        stickyFooterCell: {
            position: 'sticky',
            bottom: 0,
            zIndex: 1,
            backgroundColor: colors.headerBg,
            borderTop: `2px solid ${colors.border}`,
            fontWeight: 'bold',
            padding: '8px 10px',
            fontSize: '12px',
            color: colors.text,
        },
        empty: {
            padding: '40px',
            textAlign: 'center',
            color: colors.subtleText,
            fontStyle: 'italic',
        },
        loadingBox: {
            padding: '40px',
            textAlign: 'center',
        },
        sortIcon: {
            fontSize: '10px',
            opacity: 0.7,
            marginLeft: '4px',
        },
        statusBadge: {
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: '10px',
            fontSize: '11px',
            fontWeight: 'bold',
            whiteSpace: 'nowrap',
        },
        yesNoYes: {
            color: '#dc3545',
            fontWeight: 'bold',
        },
        yesNoNo: {
            color: colors.subtleText,
        },
        sectionWrap: {
            marginBottom: '20px',
        },
        uploadInfoBar: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexWrap: 'wrap',
            padding: '8px 20px',
            borderBottom: `1px solid ${colors.border}`,
            backgroundColor: isDark ? '#1a1a1a' : '#fafbfc',
            fontSize: '12px',
        },
        uploadInfoLabel: {
            color: colors.subtleText,
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.4px',
        },
        uploadInfoChip: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            padding: '3px 8px',
            borderRadius: '12px',
            border: '1.5px solid',
            backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : '#fff',
            fontSize: '12px',
        },
        uploadInfoEmpty: {
            color: colors.subtleText,
            fontStyle: 'italic',
        },
        summaryBody: {
            display: 'flex',
            gap: '16px',
            padding: '16px',
            flexWrap: 'wrap',
            alignItems: 'stretch',
        },
        summaryTableSide: {
            flex: '2.2 1 560px',
            minWidth: '0',
            minHeight: '560px',
            display: 'flex',
            flexDirection: 'column',
        },
        summaryStatsSide: {
            flex: '1 1 360px',
            minWidth: '320px',
            minHeight: '560px',
            display: 'flex',
        },
        statsGrid: {
            display: 'grid',
            gridTemplateColumns: '1.05fr 1fr 1fr',
            gridTemplateRows: 'repeat(10, 1fr)',
            gap: '10px',
            width: '100%',
            minHeight: '560px',
        },
        statCard: {
            borderRadius: '10px',
            padding: '10px 12px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#212529',
            boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
            textAlign: 'center',
        },
        statNumber: {
            fontSize: '40px',
            fontWeight: 'bold',
            lineHeight: 1,
        },
        statLabel: {
            fontSize: '13px',
            marginTop: '6px',
            fontWeight: '600',
        },
        statSublabel: {
            fontSize: '11px',
            marginTop: '2px',
            opacity: 0.85,
        },
        bucketCard: {
            borderRadius: '10px',
            padding: '10px 12px',
            color: '#fff',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
        },
        bucketHeader: {
            fontSize: '13px',
            fontWeight: 'bold',
            letterSpacing: '0.2px',
        },
        bucketStatRow: {
            display: 'flex',
            gap: '8px',
            flex: 1,
        },
        bucketStat: {
            flex: 1,
            backgroundColor: isDark ? 'rgba(255,255,255,0.92)' : '#ffffff',
            color: '#212529',
            borderRadius: '6px',
            padding: '6px 4px',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
        },
        bucketStatNumber: {
            fontSize: '22px',
            fontWeight: 'bold',
            lineHeight: 1,
        },
        bucketStatLabel: {
            fontSize: '10px',
            color: '#666',
            marginTop: '4px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
        },
    };
};

const formatDate = (d) => {
    if (!d) return '';
    return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
};

// Parse a date string (YYYY-MM-DD) to a Date object at local midnight.
// Returns null if not parseable.
const parseDateOnly = (s) => {
    if (!s) return null;
    const str = String(s).slice(0, 10);
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
    if (!m) {
        const d = new Date(str);
        return isNaN(d.getTime()) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate());
    }
    return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
};

const todayLocal = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

// Returns { dateStr, rel, ageDays } where rel is a short human label like
// "today", "1 day ago", "3 days ago", "1 month ago".
const formatUploadInfo = (d) => {
    if (!d) return null;
    const dateStr = `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
    const ms = Date.now() - d.getTime();
    const days = Math.floor(ms / 86400000);
    let rel;
    if (ms < 0) {
        rel = 'in the future';
    } else if (days < 1) {
        const hours = Math.floor(ms / 3600000);
        if (hours < 1) rel = 'just now';
        else rel = `${hours} hour${hours === 1 ? '' : 's'} ago`;
    } else if (days < 30) {
        rel = days === 1 ? '1 day ago' : `${days} days ago`;
    } else {
        const months = Math.floor(days / 30);
        rel = months === 1 ? '1 month ago' : `${months} months ago`;
    }
    return { dateStr, rel, ageDays: days };
};

// Color for the upload-date chip based on age in days.
const uploadAgeColor = (days) => {
    if (days == null || days < 0) return '#6c757d';
    if (days < 7) return '#3a8a4d';   // green: fresh
    if (days < 30) return '#d97a2e';  // orange: getting stale
    return '#b53a3a';                  // red: stale
};

// Color for "Days Since LFD" — anything 30+ days is concerning.
const getLfdColor = (days) => {
    if (days == null || days < 0) return '#6c757d';
    if (days < 7) return '#3a8a4d';    // green: flew within the last week
    if (days < 14) return '#d9b630';   // yellow: 1-2 weeks
    if (days < 30) return '#d97a2e';   // orange: 2-4 weeks
    return '#b53a3a';                   // red: 30+ days
};

// Returns a {bg, fg} pair for the Single Hit card based on the percentage of
// assigned aircraft that are single hits. Thresholds:
//   <3%   green
//   3-5%  yellow-green
//   5-10% yellow
//   10-15% orange
//   >=15% red
// When pct is null (denominator unknown), falls back to the original red.
const getSingleHitColor = (pct) => {
    if (pct == null) return { bg: '#c8505a', fg: '#ffffff' };
    if (pct < 3)     return { bg: '#3a8a4d', fg: '#ffffff' }; // green
    if (pct < 5)     return { bg: '#7aa83b', fg: '#ffffff' }; // yellow-green
    if (pct < 10)    return { bg: '#d9b630', fg: '#212529' }; // yellow
    if (pct < 15)    return { bg: '#d97a2e', fg: '#ffffff' }; // orange
    return                  { bg: '#b53a3a', fg: '#ffffff' }; // red
};

// Color thresholds for the AC Affected card.
//   <10%   green
//   10-20% yellow-green
//   20-30% yellow
//   30-40% orange (yellow-red)
//   >=40%  red
const getAcAffectedColor = (pct) => {
    if (pct == null) return { bg: '#e08566', fg: '#ffffff' };
    if (pct < 10)    return { bg: '#3a8a4d', fg: '#ffffff' }; // green
    if (pct < 20)    return { bg: '#7aa83b', fg: '#ffffff' }; // yellow-green
    if (pct < 30)    return { bg: '#d9b630', fg: '#212529' }; // yellow
    if (pct < 40)    return { bg: '#d97a2e', fg: '#ffffff' }; // orange
    return                  { bg: '#b53a3a', fg: '#ffffff' }; // red
};

// Small bar showing the most recent NMCS PMCS upload date per site so the
// user can gauge how fresh the data is. Color-coded by age:
//   <7 days  green, <30 days orange, otherwise red.
function UploadFreshnessBar({ styles, uploadDates, loading }) {
    if (loading) return null;
    const entries = Array.from(uploadDates.entries()).sort(([a], [b]) => a.localeCompare(b));
    return (
        <div style={styles.uploadInfoBar}>
            <span style={styles.uploadInfoLabel}>Data last uploaded:</span>
            {entries.length === 0 ? (
                <span style={styles.uploadInfoEmpty}>No upload date available</span>
            ) : (
                entries.map(([site, d]) => {
                    const info = formatUploadInfo(d);
                    const color = uploadAgeColor(info.ageDays);
                    return (
                        <span
                            key={site}
                            style={{ ...styles.uploadInfoChip, borderColor: color, color }}
                            title={`${site} — ${d.toLocaleString()}`}
                        >
                            <strong>{site}</strong>
                            <span>{info.dateStr}</span>
                            <span style={{ opacity: 0.85 }}>({info.rel})</span>
                        </span>
                    );
                })
            )}
        </div>
    );
}

// Cards displayed on the right side of the AK0 Summary table.
// Layout (3-column, 8-row grid):
//   Col 1 (rows 1-2 / 3-4 / 5-6 / 7-8): EDD bucket cards
//   Col 2 (rows 1-4 / 5-8): Single Hit, Total Parts
//   Col 3 (rows 1-4 / 5-8): AC Affected, EDD Expired
function SummaryStatsPanel({ styles, stats }) {
    const bucketColors = {
        due30:     { bg: '#3a8a4d', label: 'Due w/in 30D' },
        due3190:   { bg: '#d97a2e', label: 'Due w/in 31-90D' },
        due90Plus: { bg: '#c9a73e', label: 'Due 90D+ out' },
        noEdd:     { bg: '#6c757d', label: 'No EDD Provided' },
        expired:   { bg: '#b53a3a', label: 'EDD Expired' },
    };

    const renderBucket = (key, gridArea) => {
        const c = bucketColors[key];
        const b = stats.buckets[key];
        return (
            <div style={{ ...styles.bucketCard, backgroundColor: c.bg, gridArea }}>
                <div style={styles.bucketHeader}>{c.label}</div>
                <div style={styles.bucketStatRow}>
                    <div style={styles.bucketStat}>
                        <div style={styles.bucketStatNumber}>{b.ak0}</div>
                        <div style={styles.bucketStatLabel}>AK0</div>
                    </div>
                    <div style={styles.bucketStat}>
                        <div style={styles.bucketStatNumber}>{b.singleHit}</div>
                        <div style={styles.bucketStatLabel}>Single Hit</div>
                    </div>
                </div>
            </div>
        );
    };

    const renderStatCard = (value, label, bg, fg, gridArea, sublabel, title) => (
        <div
            style={{ ...styles.statCard, backgroundColor: bg, color: fg || '#212529', gridArea }}
            title={title}
        >
            <div style={styles.statNumber}>{value}</div>
            <div style={styles.statLabel}>{label}</div>
            {sublabel && <div style={styles.statSublabel}>{sublabel}</div>}
        </div>
    );

    const sh = getSingleHitColor(stats.singleHitPct);
    const shSub = stats.assignedAircraft > 0
        ? `${stats.singleHitPct.toFixed(1)}% of ${stats.assignedAircraft} A/C`
        : 'No assigned aircraft';
    const shTitle = stats.assignedAircraft > 0
        ? `${stats.singleHit} single hit / ${stats.assignedAircraft} assigned aircraft = ${stats.singleHitPct.toFixed(1)}%`
        : 'Aircraft assignment count unavailable';

    const ac = getAcAffectedColor(stats.acAffectedPct);
    const acSub = stats.assignedAircraft > 0
        ? `${stats.acAffectedPct.toFixed(1)}% of ${stats.assignedAircraft} A/C`
        : 'No assigned aircraft';
    const acTitle = stats.assignedAircraft > 0
        ? `${stats.acAffected} affected / ${stats.assignedAircraft} assigned aircraft = ${stats.acAffectedPct.toFixed(1)}%`
        : 'Aircraft assignment count unavailable';

    return (
        <div style={styles.statsGrid}>
            {/* Column 1: EDD bucket cards (each spans 2 of 10 rows) */}
            {renderBucket('due30',     '1 / 1 / 3 / 2')}
            {renderBucket('due3190',   '3 / 1 / 5 / 2')}
            {renderBucket('due90Plus', '5 / 1 / 7 / 2')}
            {renderBucket('noEdd',     '7 / 1 / 9 / 2')}
            {renderBucket('expired',   '9 / 1 / 11 / 2')}

            {/* Column 2: Single Hit (top), Total Parts (bottom) — each spans 5 rows */}
            {renderStatCard(stats.singleHit,  'Single Hit',  sh.bg,     sh.fg,    '1 / 2 / 6 / 3',  shSub, shTitle)}
            {renderStatCard(stats.totalParts, 'Total Parts', '#dccc62', '#212529','6 / 2 / 11 / 3')}

            {/* Column 3: AC Affected (top), EDD Expired (bottom) — each spans 5 rows */}
            {renderStatCard(stats.acAffected, 'AC Affected', ac.bg,     ac.fg,    '1 / 3 / 6 / 4',  acSub, acTitle)}
            {renderStatCard(stats.eddExpired, 'EDD Expired', '#b53a3a', '#fff',   '6 / 3 / 11 / 4')}
        </div>
    );
}

function AK0SupplyDataPage({ theme, embedded = false }) {
    const styles = getStyles(theme);
    const siteNames = useSiteNames();
    const siteOptions = [ALL_SITES, ...siteNames];
    const [siteFilter, setSiteFilter] = useState(ALL_SITES);
    const [rawData, setRawData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortField, setSortField] = useState('singleHit');
    const [sortDirection, setSortDirection] = useState('desc');
    const [alert, setAlert] = useState({ show: false, message: '', type: '' });
    const [exporting, setExporting] = useState(false);

    // Aircraft counts per site, used to compute the Single Hit % threshold.
    const [aircraftCounts, setAircraftCounts] = useState({});

    // Last Fly Date per BUNO (string keyed by BUNO from /aircraft-status/)
    const [lastFlyByBuno, setLastFlyByBuno] = useState({});

    // Details-table specific state
    const [detailsSearch, setDetailsSearch] = useState('');
    const [detailsStatusFilter, setDetailsStatusFilter] = useState('');
    const [detailsSingleHitFilter, setDetailsSingleHitFilter] = useState(''); // '', 'Yes', 'No'
    const [detailsPartTypeFilter, setDetailsPartTypeFilter] = useState('');   // '', 'Consumable', 'Repairable', 'Unknown'
    const [detailsSortField, setDetailsSortField] = useState('eddSortKey');
    const [detailsSortDirection, setDetailsSortDirection] = useState('asc');

    const fetchData = useCallback(() => {
        setLoading(true);
        const params = new URLSearchParams();
        if (siteFilter && siteFilter !== ALL_SITES) {
            params.append('site_name', siteFilter);
        }
        const qs = params.toString();
        api.get(`/nmcs-pmcs/${qs ? `?${qs}` : ''}`)
            .then(res => {
                const data = Array.isArray(res.data) ? res.data : (res.data.results || []);
                setRawData(data);
            })
            .catch(err => {
                console.error('Error fetching NMCS PMCS data:', err);
                setAlert({ show: true, message: 'Failed to fetch NMCS PMCS data.', type: 'error' });
                setRawData([]);
            })
            .finally(() => setLoading(false));
    }, [siteFilter]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Fetch aircraft list once to compute assigned-aircraft counts per site.
    useEffect(() => {
        api.get('/aircraft/')
            .then(res => {
                const list = Array.isArray(res.data) ? res.data : (res.data.results || []);
                const counts = {};
                for (const ac of list) {
                    const name = (ac.site_name || '').toString().trim();
                    if (!name) continue;
                    counts[name] = (counts[name] || 0) + 1;
                }
                setAircraftCounts(counts);
            })
            .catch(err => {
                console.error('Error fetching aircraft list:', err);
            });
    }, []);

    // Fetch aircraft status to build a BUNO -> last_fly_date map for LFD column.
    useEffect(() => {
        api.get('/aircraft-status/')
            .then(res => {
                const list = Array.isArray(res.data) ? res.data : (res.data.results || []);
                const map = {};
                for (const s of list) {
                    const buno = (s.buno || '').toString().trim();
                    if (!buno) continue;
                    if (s.last_fly_date) map[buno] = s.last_fly_date;
                }
                setLastFlyByBuno(map);
            })
            .catch(err => {
                console.error('Error fetching aircraft status:', err);
            });
    }, []);

    // Denominator for Single Hit % — total assigned aircraft for the selected site.
    const assignedAircraft = useMemo(() => {
        if (siteFilter === ALL_SITES) {
            return Object.values(aircraftCounts).reduce((a, b) => a + b, 0);
        }
        return aircraftCounts[siteFilter] || 0;
    }, [aircraftCounts, siteFilter]);

    // Latest uploaded_at timestamp per site_name. Since each upload replaces
    // all records for a site, every record for one site shares the same
    // uploaded_at within milliseconds; we still take the max defensively.
    const uploadDatesBySite = useMemo(() => {
        const map = new Map();
        for (const row of (rawData || [])) {
            const site = (row.site_name || '').toString().trim();
            if (!site) continue;
            const ts = row.uploaded_at;
            if (!ts) continue;
            const d = new Date(ts);
            if (isNaN(d.getTime())) continue;
            const cur = map.get(site);
            if (!cur || d > cur) map.set(site, d);
        }
        return map;
    }, [rawData]);

    // Pre-compute, for each aircraft (site::modex), open AK0 requisitions keyed by
    // JCN+MCN. Single Hit = exactly one requisition line (same part on two JCN/MCN
    // pairs is NOT a single hit).
    const aircraftRequisitionMap = useMemo(
        () => buildAircraftAk0RequisitionMap(filterAk0Rows(rawData)),
        [rawData]
    );

    const isAircraftSingleHitForPart = useCallback((aircraftKey, partNo) => {
        return isAircraftSingleHitForPartAgg(aircraftRequisitionMap.get(aircraftKey), partNo);
    }, [aircraftRequisitionMap]);

    // Build the AK0 Summary rows from raw NMCSPMCS records.
    // Filters to records where proj === "AK0" and groups by part_no.
    const summary = useMemo(() => {
        const today = todayLocal();
        const ak0Rows = filterAk0Rows(rawData);

        // Group by part_no (trimmed). Track first nomenclature seen and per-modex
        // record counts and the latest EDD seen for that aircraft+part.
        const groups = new Map();
        for (const row of ak0Rows) {
            const partNo = (row.part_no || '').toString().trim();
            if (!partNo) continue;
            const modex = (row.modex || '').toString().trim() || '__UNKNOWN__';
            const site = (row.site_name || '').toString().trim() || '__UNKNOWN_SITE__';
            // Key by site+modex so aircraft with the same modex at different sites
            // are counted as distinct when viewing ALL sites.
            const aircraftKey = `${site}::${modex}`;

            let g = groups.get(partNo);
            if (!g) {
                g = {
                    partNo,
                    nomen: (row.nomen || '').toString().trim(),
                    total: 0,
                    perAircraft: new Map(), // aircraftKey -> { latestEdd }
                };
                groups.set(partNo, g);
            } else if (!g.nomen && row.nomen) {
                g.nomen = row.nomen.toString().trim();
            }

            g.total += 1;

            let pa = g.perAircraft.get(aircraftKey);
            if (!pa) {
                pa = { latestEdd: null };
                g.perAircraft.set(aircraftKey, pa);
            }
            const edd = parseDateOnly(row.est_delv_dt);
            if (edd && (!pa.latestEdd || edd > pa.latestEdd)) {
                pa.latestEdd = edd;
            }
        }

        const rows = [];
        for (const g of groups.values()) {
            let singleHit = 0;
            let singleHitPastEdd = 0;
            for (const [aircraftKey, pa] of g.perAircraft.entries()) {
                if (isAircraftSingleHitForPart(aircraftKey, g.partNo)) {
                    singleHit += 1;
                    if (pa.latestEdd && pa.latestEdd < today) {
                        singleHitPastEdd += 1;
                    }
                }
            }
            rows.push({
                partNo: g.partNo,
                nomen: g.nomen || '',
                partType: lookupPartType(g.partNo) || 'Unknown',
                total: g.total,
                singleHit,
                singleHitPastEdd,
            });
        }
        return rows;
    }, [rawData, isAircraftSingleHitForPart]);

    const filteredRows = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return summary;
        return summary.filter(r =>
            r.partNo.toLowerCase().includes(term) ||
            (r.nomen || '').toLowerCase().includes(term)
        );
    }, [summary, searchTerm]);

    // Build "AK0 Details": one row per NMCSPMCS record where proj === "AK0".
    // Single Hit = Yes only when the aircraft has exactly one AK0 requisition (JCN/MCN).
    const detailsRows = useMemo(() => {
        const today = todayLocal();
        const ak0Rows = filterAk0Rows(rawData);

        const HUGE_FUTURE = Number.MAX_SAFE_INTEGER;
        return ak0Rows.map((row, idx) => {
            const partNo = (row.part_no || '').toString().trim();
            const modex = (row.modex || '').toString().trim();
            const site = (row.site_name || '').toString().trim();
            const aircraftKey = `${site || '__UNKNOWN_SITE__'}::${modex || '__UNKNOWN__'}`;
            const isSingleHit = partNo
                ? isAircraftSingleHitForPart(aircraftKey, partNo)
                : false;

            const edd = parseDateOnly(row.est_delv_dt);
            let eddStatus;
            let daysOut;
            if (!edd) {
                eddStatus = EDD_STATUS.NO_EDD;
                daysOut = null;
            } else {
                const ms = edd.getTime() - today.getTime();
                daysOut = Math.round(ms / (1000 * 60 * 60 * 24));
                if (daysOut < 0) eddStatus = EDD_STATUS.EXPIRED;
                else if (daysOut <= 30) eddStatus = EDD_STATUS.DUE_30;
                else if (daysOut <= 60) eddStatus = EDD_STATUS.DUE_60;
                else if (daysOut <= 90) eddStatus = EDD_STATUS.DUE_90;
                else eddStatus = EDD_STATUS.DUE_90_PLUS;
            }

            const ordDt = parseDateOnly(row.ord_dt);
            const rqnDt = parseDateOnly(row.rqn_dt);
            // BUNO comes from the NMCSPMCS "serno" field; fall back to modex
            // if serno is missing on a row so the column is never blank.
            const serno = (row.serno || '').toString().trim();
            const buno = serno || modex;

            // Last Fly Date is sourced from /aircraft-status/ keyed by BUNO.
            // We try the displayed BUNO first, then the raw serno, in case the
            // status board stores it under a slightly different key.
            const lfdRaw = lastFlyByBuno[buno] || lastFlyByBuno[serno] || null;
            const lfd = parseDateOnly(lfdRaw);
            const daysSinceLfd = lfd
                ? Math.max(0, Math.floor((today.getTime() - lfd.getTime()) / 86400000))
                : null;

            return {
                id: idx,
                site,
                buno,
                modex,
                partNo,
                partType: lookupPartType(partNo) || 'Unknown',
                nomen: (row.nomen || '').toString().trim(),
                isSingleHit,
                edd,
                eddStr: formatDate(edd),
                eddStatus,
                daysOut,
                ordDt,
                ordDtStr: formatDate(ordDt),
                ordDtSortKey: ordDt ? ordDt.getTime() : HUGE_FUTURE,
                rqnDt,
                rqnDtStr: formatDate(rqnDt),
                rqnDtSortKey: rqnDt ? rqnDt.getTime() : HUGE_FUTURE,
                lfd,
                lfdStr: formatDate(lfd),
                lfdSortKey: lfd ? lfd.getTime() : HUGE_FUTURE,
                daysSinceLfd,
                // Sort key for "Days Since LFD" — when LFD is missing,
                // sort to the end regardless of direction.
                daysSinceLfdSortKey: daysSinceLfd == null ? -1 : daysSinceLfd,
                // Sort key: records without an EDD sort to the end when ascending.
                eddSortKey: edd ? edd.getTime() : HUGE_FUTURE,
            };
        });
    }, [rawData, isAircraftSingleHitForPart, lastFlyByBuno]);

    const filteredDetails = useMemo(() => {
        const term = detailsSearch.trim().toLowerCase();
        return detailsRows.filter(r => {
            if (term) {
                const hay = `${r.buno} ${r.partNo} ${r.nomen} ${r.site}`.toLowerCase();
                if (!hay.includes(term)) return false;
            }
            if (detailsStatusFilter && r.eddStatus !== detailsStatusFilter) return false;
            if (detailsSingleHitFilter === 'Yes' && !r.isSingleHit) return false;
            if (detailsSingleHitFilter === 'No' && r.isSingleHit) return false;
            if (detailsPartTypeFilter && r.partType !== detailsPartTypeFilter) return false;
            return true;
        });
    }, [detailsRows, detailsSearch, detailsStatusFilter, detailsSingleHitFilter, detailsPartTypeFilter]);

    const detailsPartTypeCounts = useMemo(() => {
        const counts = {};
        PART_TYPE_OPTIONS.forEach(p => { counts[p] = 0; });
        detailsRows.forEach(r => { counts[r.partType] = (counts[r.partType] || 0) + 1; });
        return counts;
    }, [detailsRows]);

    const sortedDetails = useMemo(() => {
        const copy = [...filteredDetails];
        const dir = detailsSortDirection === 'asc' ? 1 : -1;
        copy.sort((a, b) => {
            let av = a[detailsSortField];
            let bv = b[detailsSortField];
            if (typeof av === 'string') av = av.toLowerCase();
            if (typeof bv === 'string') bv = bv.toLowerCase();
            if (av == null && bv == null) return 0;
            if (av == null) return 1;
            if (bv == null) return -1;
            if (av < bv) return -1 * dir;
            if (av > bv) return 1 * dir;
            return 0;
        });
        return copy;
    }, [filteredDetails, detailsSortField, detailsSortDirection]);

    const detailsStatusCounts = useMemo(() => {
        const counts = {};
        EDD_STATUS_OPTIONS.forEach(s => { counts[s] = 0; });
        detailsRows.forEach(r => { counts[r.eddStatus] = (counts[r.eddStatus] || 0) + 1; });
        return counts;
    }, [detailsRows]);

    // Headline stats and EDD-bucket breakdown for the right-side panel.
    //   Single Hit (total) = aircraft with exactly one AK0 requisition (unique JCN/MCN).
    const summaryStats = useMemo(() => {
        const today = todayLocal();

        const eddBucket = (d) => {
            if (!d) return EDD_STATUS.NO_EDD;
            const days = Math.round((d.getTime() - today.getTime()) / 86400000);
            if (days < 0) return EDD_STATUS.EXPIRED;
            if (days <= 30) return EDD_STATUS.DUE_30;
            if (days <= 60) return EDD_STATUS.DUE_60;
            if (days <= 90) return EDD_STATUS.DUE_90;
            return EDD_STATUS.DUE_90_PLUS;
        };
        const inDue3190 = (s) => s === EDD_STATUS.DUE_60 || s === EDD_STATUS.DUE_90;

        const aircraftMap = aircraftRequisitionMap;
        const ak0Rows = filterAk0Rows(rawData);

        // Record-level counts (used for per-bucket AK0 totals + EDD Expired).
        const recCounts = { due30: 0, due3190: 0, due90Plus: 0, noEdd: 0 };
        let totalParts = 0;
        let eddExpired = 0;
        for (const row of ak0Rows) {
            const partNo = (row.part_no || '').toString().trim();
            if (!partNo) continue;
            totalParts += 1;
            const s = eddBucket(parseDateOnly(row.est_delv_dt));
            if (s === EDD_STATUS.DUE_30) recCounts.due30 += 1;
            else if (inDue3190(s)) recCounts.due3190 += 1;
            else if (s === EDD_STATUS.DUE_90_PLUS) recCounts.due90Plus += 1;
            else if (s === EDD_STATUS.NO_EDD) recCounts.noEdd += 1;
            else if (s === EDD_STATUS.EXPIRED) eddExpired += 1;
        }

        // Aircraft-level counts.
        let singleHit = 0;
        const shByBucket = { due30: 0, due3190: 0, due90Plus: 0, noEdd: 0, expired: 0 };
        for (const agg of aircraftMap.values()) {
            if (isAircraftSingleHitAgg(agg)) {
                singleHit += 1;
                const s = eddBucket(agg.latestEdd);
                if (s === EDD_STATUS.DUE_30) shByBucket.due30 += 1;
                else if (inDue3190(s)) shByBucket.due3190 += 1;
                else if (s === EDD_STATUS.DUE_90_PLUS) shByBucket.due90Plus += 1;
                else if (s === EDD_STATUS.NO_EDD) shByBucket.noEdd += 1;
                else if (s === EDD_STATUS.EXPIRED) shByBucket.expired += 1;
            }
        }

        const singleHitPct = assignedAircraft > 0 ? (singleHit / assignedAircraft) * 100 : null;
        const acAffectedPct = assignedAircraft > 0 ? (aircraftMap.size / assignedAircraft) * 100 : null;

        return {
            totalParts,
            singleHit,
            singleHitPct,
            assignedAircraft,
            acAffected: aircraftMap.size,
            acAffectedPct,
            eddExpired,
            buckets: {
                due30:     { ak0: recCounts.due30,     singleHit: shByBucket.due30 },
                due3190:   { ak0: recCounts.due3190,   singleHit: shByBucket.due3190 },
                due90Plus: { ak0: recCounts.due90Plus, singleHit: shByBucket.due90Plus },
                noEdd:     { ak0: recCounts.noEdd,     singleHit: shByBucket.noEdd },
                expired:   { ak0: eddExpired,          singleHit: shByBucket.expired },
            },
        };
    }, [rawData, assignedAircraft, aircraftRequisitionMap]);

    const sortedRows = useMemo(() => {
        const copy = [...filteredRows];
        const dir = sortDirection === 'asc' ? 1 : -1;
        copy.sort((a, b) => {
            let av = a[sortField];
            let bv = b[sortField];
            if (typeof av === 'string') av = av.toLowerCase();
            if (typeof bv === 'string') bv = bv.toLowerCase();
            if (av < bv) return -1 * dir;
            if (av > bv) return 1 * dir;
            return 0;
        });
        return copy;
    }, [filteredRows, sortField, sortDirection]);

    const totals = useMemo(() => {
        return sortedRows.reduce(
            (acc, r) => {
                acc.total += r.total;
                acc.singleHit += r.singleHit;
                acc.singleHitPastEdd += r.singleHitPastEdd;
                return acc;
            },
            { total: 0, singleHit: 0, singleHitPastEdd: 0 }
        );
    }, [sortedRows]);

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(d => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(field);
            setSortDirection(field === 'partNo' || field === 'nomen' ? 'asc' : 'desc');
        }
    };

    const sortIcon = (field) => {
        if (sortField !== field) return null;
        return <span style={styles.sortIcon}>{sortDirection === 'asc' ? '▲' : '▼'}</span>;
    };

    const handleDetailsSort = (field) => {
        if (detailsSortField === field) {
            setDetailsSortDirection(d => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setDetailsSortField(field);
            setDetailsSortDirection(field === 'eddSortKey' ? 'asc' : 'asc');
        }
    };

    const detailsSortIcon = (field) => {
        if (detailsSortField !== field) return null;
        return <span style={styles.sortIcon}>{detailsSortDirection === 'asc' ? '▲' : '▼'}</span>;
    };

    // Clicking a Part Number in the Summary table filters the Details table.
    const handleSummaryPartClick = (partNo) => {
        setDetailsSearch(partNo);
        setDetailsStatusFilter('');
        setDetailsSingleHitFilter('');
        setDetailsPartTypeFilter('');
        // Defer scroll until React re-renders.
        setTimeout(() => {
            const el = document.getElementById('ak0-details-section');
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 0);
    };

    const handleExport = async () => {
        try {
            setExporting(true);
            setAlert({ show: true, message: 'Generating Excel file...', type: 'info' });

            const workbook = new ExcelJS.Workbook();
            const sheet = workbook.addWorksheet('AK0 Summary');

            const titleSuffix = siteFilter === ALL_SITES ? 'All Sites' : siteFilter;
            sheet.addRow([`AK0 Supply Summary - ${titleSuffix}`]);
            sheet.mergeCells(1, 1, 1, 6);
            const titleCell = sheet.getCell('A1');
            titleCell.font = { size: 14, bold: true };
            titleCell.alignment = { horizontal: 'center' };

            sheet.addRow([]);

            const headers = ['Part Number', 'Nomenclature', 'Part Type', 'AK0', 'Single Hit', 'Single Hit + Past EDD'];
            const headerRow = sheet.addRow(headers);
            headerRow.font = { bold: true };
            headerRow.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFD3D3D3' },
            };
            headerRow.alignment = { horizontal: 'center', vertical: 'middle' };

            sortedRows.forEach((r, i) => {
                const row = sheet.addRow([r.partNo, r.nomen, r.partType, r.total, r.singleHit, r.singleHitPastEdd]);
                if (i % 2 === 0) {
                    row.eachCell(cell => {
                        cell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: 'FFF2F2F2' },
                        };
                    });
                }
                const ptCell = row.getCell(3);
                const ptArgb = (PART_TYPE_COLORS[r.partType] || PART_TYPE_COLORS.Unknown).argb;
                ptCell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: ptArgb },
                };
                ptCell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                ptCell.alignment = { horizontal: 'center' };
            });

            const totalRow = sheet.addRow(['Total', '', '', totals.total, totals.singleHit, totals.singleHitPastEdd]);
            totalRow.font = { bold: true };
            totalRow.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFD3D3D3' },
            };

            sheet.columns = [
                { width: 22 }, // Part Number
                { width: 40 }, // Nomenclature
                { width: 14 }, // Part Type
                { width: 10 }, // AK0
                { width: 14 }, // Single Hit
                { width: 22 }, // Single Hit + Past EDD
            ];

            sheet.eachRow(row => {
                row.eachCell(cell => {
                    cell.border = {
                        top: { style: 'thin' },
                        left: { style: 'thin' },
                        bottom: { style: 'thin' },
                        right: { style: 'thin' },
                    };
                });
            });

            // -- AK0 Details sheet --
            const detailsSheet = workbook.addWorksheet('AK0 Details');

            detailsSheet.addRow([`AK0 Details - ${titleSuffix}`]);
            detailsSheet.mergeCells(1, 1, 1, 12);
            const dTitleCell = detailsSheet.getCell('A1');
            dTitleCell.font = { size: 14, bold: true };
            dTitleCell.alignment = { horizontal: 'center' };
            detailsSheet.addRow([]);

            const detailsHeaders = ['Site', 'BUNO', 'Part Number', 'Nomenclature', 'LFD', 'Days Since LFD', 'Part Type', 'Single Hit', 'Order Date', 'COMB Acceptance', 'EDD', 'EDD Status'];
            const dHeaderRow = detailsSheet.addRow(detailsHeaders);
            dHeaderRow.font = { bold: true };
            dHeaderRow.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFD3D3D3' },
            };
            dHeaderRow.alignment = { horizontal: 'center', vertical: 'middle' };

            sortedDetails.forEach((r, i) => {
                const row = detailsSheet.addRow([
                    r.site,
                    r.buno,
                    r.partNo,
                    r.nomen,
                    r.lfdStr,
                    r.daysSinceLfd == null ? '' : r.daysSinceLfd,
                    r.partType,
                    r.isSingleHit ? 'Yes' : 'No',
                    r.ordDtStr,
                    r.rqnDtStr,
                    r.eddStr,
                    r.eddStatus,
                ]);
                if (i % 2 === 0) {
                    row.eachCell(cell => {
                        cell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: 'FFF2F2F2' },
                        };
                    });
                }
                if (r.daysSinceLfd != null) {
                    const dsCell = row.getCell(6);
                    const hex = getLfdColor(r.daysSinceLfd).replace('#', '').toUpperCase();
                    dsCell.font = {
                        bold: r.daysSinceLfd >= 30,
                        color: { argb: `FF${hex}` },
                    };
                    dsCell.alignment = { horizontal: 'right' };
                }

                const ptCell = row.getCell(7);
                const ptArgb = (PART_TYPE_COLORS[r.partType] || PART_TYPE_COLORS.Unknown).argb;
                ptCell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: ptArgb },
                };
                ptCell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                ptCell.alignment = { horizontal: 'center' };

                const statusCell = row.getCell(12);
                const argb = (EDD_STATUS_COLORS[r.eddStatus] || {}).argb;
                if (argb) {
                    statusCell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb },
                    };
                    statusCell.font = {
                        bold: true,
                        color: { argb: r.eddStatus === EDD_STATUS.DUE_60 ? 'FF212529' : 'FFFFFFFF' },
                    };
                    statusCell.alignment = { horizontal: 'center' };
                }
            });

            detailsSheet.columns = [
                { width: 10 }, // Site
                { width: 10 }, // BUNO
                { width: 22 }, // Part Number
                { width: 40 }, // Nomenclature
                { width: 12 }, // LFD
                { width: 14 }, // Days Since LFD
                { width: 14 }, // Part Type
                { width: 12 }, // Single Hit
                { width: 12 }, // Order Date
                { width: 16 }, // COMB Acceptance
                { width: 12 }, // EDD
                { width: 22 }, // EDD Status
            ];

            detailsSheet.eachRow(row => {
                row.eachCell(cell => {
                    cell.border = {
                        top: { style: 'thin' },
                        left: { style: 'thin' },
                        bottom: { style: 'thin' },
                        right: { style: 'thin' },
                    };
                });
            });

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const fileSuffix = siteFilter === ALL_SITES ? 'AllSites' : siteFilter;
            link.download = `AK0_Supply_Summary_${fileSuffix}_${new Date().toISOString().split('T')[0]}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            setAlert({ show: true, message: 'Excel file exported successfully.', type: 'success' });
        } catch (err) {
            console.error('Error exporting AK0 summary:', err);
            setAlert({ show: true, message: 'Failed to export Excel file.', type: 'error' });
        } finally {
            setExporting(false);
        }
    };

    return (
        <div style={embedded ? undefined : styles.page}>
            <CustomAlert {...alert} onClose={() => setAlert({ show: false })} theme={theme} />

            {!embedded && (
                <div style={styles.headerCard}>
                    <Link to="/" style={styles.navLink}>&larr; Back to Home</Link>
                    <h1 style={styles.title}>AK0 Supply Data</h1>
                    <p style={styles.subtitle}>
                        Summary of AK0 supply hits derived from the most recent NMCS PMCS upload for the selected site.
                    </p>
                </div>
            )}

            <div style={{ ...styles.section, ...styles.sectionWrap }}>
                <div style={styles.sectionHeader}>
                    <h2 style={styles.sectionTitle}>AK0 Summary</h2>
                    <button
                        onClick={handleExport}
                        disabled={exporting || loading || (sortedRows.length === 0 && sortedDetails.length === 0)}
                        style={{
                            ...styles.exportButton,
                            opacity: (exporting || loading || (sortedRows.length === 0 && sortedDetails.length === 0)) ? 0.6 : 1,
                            cursor: (exporting || loading || (sortedRows.length === 0 && sortedDetails.length === 0)) ? 'not-allowed' : 'pointer',
                        }}
                    >
                        {exporting ? 'Exporting...' : 'Export to Excel'}
                    </button>
                </div>

                <UploadFreshnessBar styles={styles} uploadDates={uploadDatesBySite} loading={loading} />

                <div style={styles.controls}>
                    <select
                        value={siteFilter}
                        onChange={(e) => setSiteFilter(e.target.value)}
                        style={styles.select}
                    >
                        {siteOptions.map(s => (
                            <option key={s} value={s}>{s === ALL_SITES ? 'All Sites' : s}</option>
                        ))}
                    </select>
                    <input
                        type="text"
                        placeholder="Search by Part Number or Nomenclature..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={styles.input}
                    />
                    <span style={{ fontSize: '12px', color: theme === 'dark' ? '#a0a0a0' : '#666' }}>
                        {sortedRows.length} parts
                    </span>
                </div>

                <div style={styles.summaryBody}>
                    <div style={styles.summaryTableSide}>
                {loading ? (
                    <div style={styles.loadingBox}>
                        <Spinner />
                    </div>
                ) : sortedRows.length === 0 ? (
                    <div style={styles.empty}>
                        No AK0 NMCS PMCS records found for {siteFilter === ALL_SITES ? 'any site' : siteFilter}. Upload NMCS PMCS data to get started.
                    </div>
                ) : (
                    <div style={styles.scrollableTableWrap}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th} onClick={() => handleSort('partNo')}>
                                        Part Number{sortIcon('partNo')}
                                    </th>
                                    <th style={styles.th} onClick={() => handleSort('nomen')}>
                                        Nomenclature{sortIcon('nomen')}
                                    </th>
                                    <th style={{ ...styles.th, textAlign: 'center' }} onClick={() => handleSort('partType')}>
                                        Part Type{sortIcon('partType')}
                                    </th>
                                    <th style={{ ...styles.th, ...styles.thNum }} onClick={() => handleSort('total')}>
                                        AK0{sortIcon('total')}
                                    </th>
                                    <th style={{ ...styles.th, ...styles.thNum }} onClick={() => handleSort('singleHit')}>
                                        Single Hit{sortIcon('singleHit')}
                                    </th>
                                    <th style={{ ...styles.th, ...styles.thNum }} onClick={() => handleSort('singleHitPastEdd')}>
                                        Single Hit + Past EDD{sortIcon('singleHitPastEdd')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedRows.map((r, idx) => (
                                    <tr key={r.partNo} style={idx % 2 === 1 ? styles.rowAlt : null}>
                                        <td style={styles.td}>
                                            <button
                                                onClick={() => handleSummaryPartClick(r.partNo)}
                                                title="Click to filter Details by this part number"
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    padding: 0,
                                                    color: theme === 'dark' ? '#6cb2f7' : '#007bff',
                                                    textDecoration: 'underline',
                                                    cursor: 'pointer',
                                                    font: 'inherit',
                                                }}
                                            >
                                                {r.partNo}
                                            </button>
                                        </td>
                                        <td style={styles.td}>{r.nomen}</td>
                                        <td style={{ ...styles.td, textAlign: 'center' }}>
                                            {(() => {
                                                const pc = PART_TYPE_COLORS[r.partType] || PART_TYPE_COLORS.Unknown;
                                                return (
                                                    <span style={{
                                                        ...styles.statusBadge,
                                                        backgroundColor: pc.bg,
                                                        color: pc.fg,
                                                    }}>
                                                        {r.partType}
                                                    </span>
                                                );
                                            })()}
                                        </td>
                                        <td style={{ ...styles.td, ...styles.tdNum }}>{r.total}</td>
                                        <td style={{ ...styles.td, ...styles.tdNum }}>{r.singleHit}</td>
                                        <td style={{ ...styles.td, ...styles.tdNum }}>
                                            {r.singleHitPastEdd > 0 ? r.singleHitPastEdd : ''}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td style={styles.stickyFooterCell}>Total ({sortedRows.length})</td>
                                    <td style={styles.stickyFooterCell}></td>
                                    <td style={styles.stickyFooterCell}></td>
                                    <td style={{ ...styles.stickyFooterCell, ...styles.tdNum }}>{totals.total}</td>
                                    <td style={{ ...styles.stickyFooterCell, ...styles.tdNum }}>{totals.singleHit}</td>
                                    <td style={{ ...styles.stickyFooterCell, ...styles.tdNum }}>{totals.singleHitPastEdd}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                )}
                    </div>

                    <div style={styles.summaryStatsSide}>
                        <SummaryStatsPanel styles={styles} stats={summaryStats} />
                    </div>
                </div>
            </div>

            <div id="ak0-details-section" style={{ ...styles.section, ...styles.sectionWrap }}>
                <div style={styles.sectionHeader}>
                    <h2 style={styles.sectionTitle}>AK0 Details</h2>
                    <span style={{ fontSize: '12px', color: theme === 'dark' ? '#a0a0a0' : '#666' }}>
                        {sortedDetails.length} of {detailsRows.length} records
                    </span>
                </div>

                <div style={styles.controls}>
                    <input
                        type="text"
                        placeholder="Search BUNO, Part Number, Nomenclature..."
                        value={detailsSearch}
                        onChange={(e) => setDetailsSearch(e.target.value)}
                        style={styles.input}
                    />
                    <select
                        value={detailsStatusFilter}
                        onChange={(e) => setDetailsStatusFilter(e.target.value)}
                        style={styles.select}
                        title="Filter by EDD Status"
                    >
                        <option value="">All EDD Statuses</option>
                        {EDD_STATUS_OPTIONS.map(s => (
                            <option key={s} value={s}>
                                {s} ({detailsStatusCounts[s] || 0})
                            </option>
                        ))}
                    </select>
                    <select
                        value={detailsSingleHitFilter}
                        onChange={(e) => setDetailsSingleHitFilter(e.target.value)}
                        style={styles.select}
                        title="Filter by Single Hit"
                    >
                        <option value="">All (Single Hit)</option>
                        <option value="Yes">Single Hit: Yes</option>
                        <option value="No">Single Hit: No</option>
                    </select>
                    <select
                        value={detailsPartTypeFilter}
                        onChange={(e) => setDetailsPartTypeFilter(e.target.value)}
                        style={styles.select}
                        title="Filter by Part Type"
                    >
                        <option value="">All Part Types</option>
                        {PART_TYPE_OPTIONS.map(p => (
                            <option key={p} value={p}>
                                {p} ({detailsPartTypeCounts[p] || 0})
                            </option>
                        ))}
                    </select>
                    {(detailsSearch || detailsStatusFilter || detailsSingleHitFilter || detailsPartTypeFilter) && (
                        <button
                            onClick={() => {
                                setDetailsSearch('');
                                setDetailsStatusFilter('');
                                setDetailsSingleHitFilter('');
                                setDetailsPartTypeFilter('');
                            }}
                            style={{
                                padding: '6px 12px',
                                background: 'transparent',
                                color: theme === 'dark' ? '#6cb2f7' : '#007bff',
                                border: `1px solid ${theme === 'dark' ? '#6cb2f7' : '#007bff'}`,
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px',
                            }}
                        >
                            Clear Filters
                        </button>
                    )}
                </div>

                {loading ? (
                    <div style={styles.loadingBox}>
                        <Spinner />
                    </div>
                ) : detailsRows.length === 0 ? (
                    <div style={styles.empty}>
                        No AK0 NMCS PMCS records found for {siteFilter === ALL_SITES ? 'any site' : siteFilter}.
                    </div>
                ) : sortedDetails.length === 0 ? (
                    <div style={styles.empty}>
                        No records match the current filters.
                    </div>
                ) : (
                    <div style={styles.tableWrap}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    {siteFilter === ALL_SITES && (
                                        <th style={styles.th} onClick={() => handleDetailsSort('site')}>
                                            Site{detailsSortIcon('site')}
                                        </th>
                                    )}
                                    <th style={styles.th} onClick={() => handleDetailsSort('buno')}>
                                        BUNO{detailsSortIcon('buno')}
                                    </th>
                                    <th style={styles.th} onClick={() => handleDetailsSort('partNo')}>
                                        Part Number{detailsSortIcon('partNo')}
                                    </th>
                                    <th style={styles.th} onClick={() => handleDetailsSort('nomen')}>
                                        Nomenclature{detailsSortIcon('nomen')}
                                    </th>
                                    <th style={styles.th} onClick={() => handleDetailsSort('lfdSortKey')} title="Last Fly Date for the aircraft (from Aircraft Status)">
                                        LFD{detailsSortIcon('lfdSortKey')}
                                    </th>
                                    <th style={{ ...styles.th, ...styles.thNum }} onClick={() => handleDetailsSort('daysSinceLfdSortKey')} title="Days since the aircraft last flew">
                                        Days Since LFD{detailsSortIcon('daysSinceLfdSortKey')}
                                    </th>
                                    <th style={{ ...styles.th, textAlign: 'center' }} onClick={() => handleDetailsSort('partType')}>
                                        Part Type{detailsSortIcon('partType')}
                                    </th>
                                    <th style={{ ...styles.th, textAlign: 'center' }} onClick={() => handleDetailsSort('isSingleHit')}>
                                        Single Hit{detailsSortIcon('isSingleHit')}
                                    </th>
                                    <th style={styles.th} onClick={() => handleDetailsSort('ordDtSortKey')} title="Order Date (ord_dt)">
                                        Order Date{detailsSortIcon('ordDtSortKey')}
                                    </th>
                                    <th style={styles.th} onClick={() => handleDetailsSort('rqnDtSortKey')} title="COMB Acceptance (rqn_dt)">
                                        COMB Acceptance{detailsSortIcon('rqnDtSortKey')}
                                    </th>
                                    <th style={styles.th} onClick={() => handleDetailsSort('eddSortKey')}>
                                        EDD{detailsSortIcon('eddSortKey')}
                                    </th>
                                    <th style={{ ...styles.th, textAlign: 'center' }} onClick={() => handleDetailsSort('eddStatus')}>
                                        EDD Status{detailsSortIcon('eddStatus')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedDetails.map((r, idx) => {
                                    const sc = EDD_STATUS_COLORS[r.eddStatus] || {};
                                    return (
                                        <tr key={r.id} style={idx % 2 === 1 ? styles.rowAlt : null}>
                                            {siteFilter === ALL_SITES && (
                                                <td style={styles.td}>{r.site}</td>
                                            )}
                                            <td style={styles.td}>{r.buno}</td>
                                            <td style={styles.td}>{r.partNo}</td>
                                            <td style={styles.td}>{r.nomen}</td>
                                            <td style={styles.td}>{r.lfdStr}</td>
                                            <td style={{ ...styles.td, ...styles.tdNum }}>
                                                {r.daysSinceLfd == null ? '' : (
                                                    <span style={{
                                                        color: getLfdColor(r.daysSinceLfd),
                                                        fontWeight: r.daysSinceLfd >= 30 ? 'bold' : 'normal',
                                                    }}>
                                                        {r.daysSinceLfd}
                                                    </span>
                                                )}
                                            </td>
                                            <td style={{ ...styles.td, textAlign: 'center' }}>
                                                {(() => {
                                                    const pc = PART_TYPE_COLORS[r.partType] || PART_TYPE_COLORS.Unknown;
                                                    return (
                                                        <span style={{
                                                            ...styles.statusBadge,
                                                            backgroundColor: pc.bg,
                                                            color: pc.fg,
                                                        }}>
                                                            {r.partType}
                                                        </span>
                                                    );
                                                })()}
                                            </td>
                                            <td style={{ ...styles.td, textAlign: 'center' }}>
                                                <span style={r.isSingleHit ? styles.yesNoYes : styles.yesNoNo}>
                                                    {r.isSingleHit ? 'Yes' : 'No'}
                                                </span>
                                            </td>
                                            <td style={styles.td}>{r.ordDtStr}</td>
                                            <td style={styles.td}>{r.rqnDtStr}</td>
                                            <td style={styles.td}>{r.eddStr}</td>
                                            <td style={{ ...styles.td, textAlign: 'center' }}>
                                                <span style={{
                                                    ...styles.statusBadge,
                                                    backgroundColor: sc.bg,
                                                    color: sc.fg,
                                                }}>
                                                    {r.eddStatus}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

export default AK0SupplyDataPage;
